<script lang="ts" setup>
    import { onMounted, ref, watch, reactive, computed} from 'vue';
    import * as ReportModel from '../../lib/Models/Reports/FinancialReportModel';
    import * as _ from 'lodash';
    import { PageTitle } from '../../LibMain';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import { NoTzDate, getNoTzToday, addDay, toNoTzDate, toDate, toDisplayDateString, startOfMonth, addMonth, endOfMonth } from '../../lib/DateUtils';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import { popAlert } from '../../lib/Notification';
    import { DateDisplayFormat, ButtonDisplaySize, LayoutOrientation } from "../../Enums/UiEnums";
    import DailyFinancialReportDrillDown from '../../ChoiceScreens/Reports/DailyFinancialReportDrillDown.vue';
    import Currency from '../../components/Formatting/Currency.vue';

    import '../../../css/choice.css';
    import 'vue-select/dist/vue-select.css';

    const props = defineProps<{
        screenName: string,
        reportRunDate: NoTzDate,
        selectedDateType: ReportModel.FinancialReportDateTypes,
        summarizedReportDataDaily?: ReportModel.IFinancialReportResults[] | any[],
        selectedGroupBy1: string,
        selectedGroupBy2?: string,
        selectedIncludeCounts?: boolean,
        selectedIncludeCharges?: boolean,
        selectedIncludePaymentsAdjustments?: boolean,
        selectedIncludeRefunds?: boolean,
        totalFields: any,
        groupByDropDownList: ReportModel.IDropDownList[],
        mainReportData?: ReportModel.IFinancialReportResults[] | any[]
    }>();

    const reportData = reactive<{             
        showDrillDown: boolean,
        detailRowItem?: any
    }>({        
        showDrillDown: false
    });

    const emit = defineEmits<{
        (e: "onShowDrillDown", showDrillDown: boolean) : void,
        (e: "onShowAdvancedSearchCriteria", showAdvancedSearchCriteria: boolean) : void
    }>();

    function formatPayments(item: any) {
        const itemValue = +item !== 0 ? (+item * -1) : +item;
        return itemValue;
    }

    async function printDailySummary() {
        try{
            document.title = 'Daily Financial Report.pdf';
            window.print();
        }
        catch(e){
            console.log("Error printing report: " + e);
        }
    }

    async function exportMainReport() {
        try {
            const wb = XLSX.utils.book_new()
            const ws1 = XLSX.utils.json_to_sheet(props.summarizedReportDataDaily);
            XLSX.utils.book_append_sheet(wb, ws1, "DailyReportData");
            XLSX.writeFile(wb, "DailyFinancialReportData.xls");
        }
        catch (e) {
            await popAlert("Error downloading data to Excel: " + e);
        }	
    }

    function getGroupByDropDownDescription(columnName: string): string | undefined {
        const aliasInfo = props.groupByDropDownList.find(
            (item) => item.columnName === columnName
        );
        return aliasInfo?.dropDownDescription;
    }


    async function showDetailReport(itemClicked: any) {
        reportData.showDrillDown = true;
        //emit("onShowDrillDown", true);
        emit("onShowAdvancedSearchCriteria", false);
        reportData.detailRowItem = itemClicked;      
    }

    async function visibilityDrillDown(showDrillDown: boolean) {
        reportData.showDrillDown = showDrillDown;
        emit("onShowDrillDown", showDrillDown);
    }

</script>

<style>
    @media print {
        .no-print { display: none; }
        body { width: 1500px; } 
}
</style>


<template>
    <PageTitle :security-screen-name="props.screenName">
        <template #permitted> 
            <div v-if="props.summarizedReportDataDaily && props.summarizedReportDataDaily.length < 1 && !reportData.showDrillDown" class="text-center fs-6"> 
                No records found matching your report criteria
            </div>
            <div v-if="props.summarizedReportDataDaily && props.summarizedReportDataDaily.length > 0 && !reportData.showDrillDown">
                <div class="row">
                    <div class="col text-end no-print mb-2">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Print"
                            :text="'Print'"
                            :size="ButtonDisplaySize.Smaller" 
                            :spinText="'Printing...'"
                            :performAction="printDailySummary"
                            :endingPadding="1"
                        />
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Export"
                            :text="'Export to Excel'"
                            :size="ButtonDisplaySize.Smaller" 
                            :spinText="'Exporting...'"
                            :performAction="exportMainReport"
                        />
                    </div>
                </div>

                <table v-if="!reportData.showDrillDown" class="table table-hover" aria-describedby="Daily Financial Report">
                    <tr class="background-isalus-primary-light label-isalus-small">
                        <th class="border-isalus-primary ps-1">
                            {{ getGroupByDropDownDescription(props.selectedGroupBy1) }}
                        </th>
                        <th class="border-isalus-primary ps-1" v-if="props.selectedGroupBy2">
                            {{ getGroupByDropDownDescription(props.selectedGroupBy2) }}
                        </th>
                        <th v-if="props.selectedIncludeCounts" class="border-isalus-primary text-center">
                            Claim<br>Count
                        </th>
                        <th v-if="props.selectedIncludeCharges" class="border-isalus-primary text-center">
                            Charges
                        </th>
                        <th v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-center">
                            Patient<br>Payments
                        </th>
                        <th v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-center">
                            Insurance<br>Payments
                        </th>
                        <th v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-center">
                            Applied<br>Payments
                        </th>
                        <th v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-center">
                            Unapplied<br>Payments
                        </th>
                        <th v-if="props.selectedIncludeRefunds" class="border-isalus-primary text-center">
                            Refund<br>Payments
                        </th>
                        <th v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-center">
                            Total<br>Payments
                        </th>
                        <th v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-center">
                            Total<br>Adjustments
                        </th>
                    </tr>
                    <tbody>
                        <tr v-for="item in props.summarizedReportDataDaily" class="label-isalus-small" style="cursor: pointer;" title="Click to view detail of this row" @click="showDetailReport(item)">
                            <td class="border-isalus-primary">
                                {{ "postDay" == props.selectedGroupBy1 ? props.reportRunDate : (item[props.selectedGroupBy1] !== null) ? item[props.selectedGroupBy1] : "" }}
                            </td>
                            <td class="border-isalus-primary" v-if="props.selectedGroupBy2">
                                {{ "postDay" == props.selectedGroupBy2 ? props.reportRunDate : item[props.selectedGroupBy2] }}
                            </td>
                            <td v-if="props.selectedIncludeCounts" class="border-isalus-primary text-end">
                                {{ item.claimCount }}
                            </td>
                            <td v-if="props.selectedIncludeCharges" class="border-isalus-primary text-end">
                                <Currency :value="item.chargeAmount"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end">
                                <Currency :value="formatPayments(item.patientPaymentTotal)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end">
                                <Currency :value="formatPayments(item.insurancePaymentTotal)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end">
                                <Currency :value="formatPayments(item.paymentTotal - item.unappliedAmount)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end">
                                <Currency :value="formatPayments(item.unappliedAmount)"/>
                            </td>
                            <td v-if="props.selectedIncludeRefunds" class="border-isalus-primary text-end">
                                <Currency :value="formatPayments(item.refundTotal)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end">
                                <Currency :value="formatPayments(item.paymentTotal)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end">
                                <Currency :value="formatPayments(item.adjustmentTotal)"/>
                            </td>
                        </tr>
                        <tr class="background-isalus-primary-light" title="Click to view detail of total" @click="showDetailReport('total')">
                            <td class="border-isalus-primary fw-bold">
                                Total
                            </td>
                            <td class="border-isalus-primary" v-if="props.selectedGroupBy2">
                                
                            </td>
                            <td v-if="props.selectedIncludeCounts" class="border-isalus-primary text-end fw-bold">
                                {{ props.totalFields.totalClaimCount }}
                            </td>
                            <td v-if="props.selectedIncludeCharges" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="props.totalFields.totalCharges"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="formatPayments(props.totalFields.totalPatientPayments)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="formatPayments(props.totalFields.totalInsurancePayments)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="formatPayments((props.totalFields.totalPayments ?? 0) - (props?.totalFields.totalUnappliedPayments ?? 0))"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="formatPayments(props.totalFields.totalUnappliedPayments)"/>
                            </td>
                            <td v-if="props.selectedIncludeRefunds" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="formatPayments(props.totalFields.totalRefundPayments)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="formatPayments(props.totalFields.totalPayments)"/>
                            </td>
                            <td v-if="props.selectedIncludePaymentsAdjustments" class="border-isalus-primary text-end fw-bold">
                                <Currency :value="formatPayments(props.totalFields.totalAdjustments)"/>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="reportData.showDrillDown && props.mainReportData">
                <DailyFinancialReportDrillDown
                    :screenName="props.screenName"
                    :reportRunDate="props.reportRunDate ?? getNoTzToday()"
                    :selectedDateType="props.selectedDateType"
                    :selectedGroupBy1="props.selectedGroupBy1"
                    :selectedGroupBy2="props.selectedGroupBy2"
                    :detailRowItem="reportData.detailRowItem"
                    :drillDownData="props.mainReportData"
                    @onShowDrillDown="visibilityDrillDown" 
                />
            </div>

        </template>
    </PageTitle>
</template>